import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { JudgeRowView, SetupResponse } from "@joogoos/contracts";
import { api } from "../../api.js";
import { Icon } from "../../ui/icons.js";
import { Card } from "./chrome.js";

/**
 * **Set up, step 3** — the scored table that decides which companies are worth writing to (23 §the judge).
 *
 * Every row is the owner's; the mechanics — points, a go, a look, a refusal that wins whatever the score — are
 * the product's, and the three refusals it always makes are shown locked so nobody hunts for the switch.
 */

export function Judge({ d, keep }: { d: SetupResponse; keep: (d: SetupResponse) => void }) {
  const [adding, setAdding] = useState<"score" | "refusal" | null>(null);
  const [fact, setFact] = useState("");
  const [points, setPoints] = useState("10");
  const save = useMutation({
    mutationFn: (v: { row: unknown; id?: string }) => api.saveJudgeRow(v.row, v.id),
    onSuccess: (r) => { keep(r); setAdding(null); setFact(""); },
  });
  const drop = useMutation({ mutationFn: (id: string) => api.removeJudgeRow(id), onSuccess: keep });
  const lines = useMutation({ mutationFn: (s: unknown) => api.saveCustomerSettings(s), onSuccess: keep });

  const score = d.judge.filter((r) => r.kind === "score");
  const refusals = d.judge.filter((r) => r.kind === "refusal");
  const outOf = score.reduce((n, r) => n + r.points, 0);

  return (
    <Card title="The judge" aside={<span className="text-xs text-muted">what makes a company worth writing to</span>}>
      <p className="text-xs text-muted leading-relaxed">
        The score decides: <b>a go at {d.settings.goAt}</b>, <b>a look at {d.settings.lookAt}</b>. A look is a
        person's decision, because not knowing is not a verdict.
      </p>
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-[10px] uppercase tracking-[.06em] text-muted text-left">
            <th className="py-1 font-semibold">A fact worth points</th>
            <th className="py-1 font-semibold text-right">points</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {score.map((r) => <Row key={r.id} row={r} mayEdit={d.mayEdit} onRemove={() => drop.mutate(r.id)} />)}
          {!score.length ? <tr><td colSpan={3} className="py-2 text-muted">Nothing scores yet.</td></tr> : null}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {d.mayEdit ? <button onClick={() => { setAdding("score"); setFact(""); }} className="text-coral font-medium">Add a row</button> : null}
        <span className="ml-auto text-muted tabular-nums">out of {outOf}</span>
        <label className="flex items-center gap-1">go at
          <input
            defaultValue={d.settings.goAt} disabled={!d.mayEdit} aria-label="Go at"
            onBlur={(e) => lines.mutate({ goAt: Number(e.target.value) || d.settings.goAt })}
            className="w-12 px-1.5 py-0.5 rounded border border-line-2 bg-page text-center tabular-nums"
          />
        </label>
        <label className="flex items-center gap-1">look at
          <input
            defaultValue={d.settings.lookAt} disabled={!d.mayEdit} aria-label="Look at"
            onBlur={(e) => lines.mutate({ lookAt: Number(e.target.value) || d.settings.lookAt })}
            className="w-12 px-1.5 py-0.5 rounded border border-line-2 bg-page text-center tabular-nums"
          />
        </label>
      </div>

      <div className="border-t border-line pt-3 flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">A no, whatever the score</span>
        {d.locked.map((g) => (
          <div key={g.code} className="flex items-center gap-2 text-[13px] py-1 border-b border-line">
            <Icon name="lock" size={12} className="text-faint" />
            <span>{g.says}</span>
            <span className="ml-auto text-xs text-faint">in the product</span>
          </div>
        ))}
        {refusals.map((r) => (
          <div key={r.id} className="flex items-center gap-2 text-[13px] py-1 border-b border-line">
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-bad text-white">no</span>
            <span>{r.fact}</span>
            {d.mayEdit ? <button onClick={() => drop.mutate(r.id)} className="ml-auto text-xs text-muted">Remove</button> : null}
          </div>
        ))}
        {d.mayEdit ? <button onClick={() => { setAdding("refusal"); setFact(""); }} className="self-start text-xs text-coral font-medium">Add a refusal</button> : null}
        <p className="text-xs text-faint leading-relaxed">
          The three at the top are in the product and cannot be turned off. Nobody can turn a refusal into a go
          without taking the fact away first, and doing that is written in the company's history.
        </p>
      </div>

      {adding ? (
        <form
          className="rounded-lg border border-line-2 bg-page p-3 flex flex-wrap items-end gap-2"
          onSubmit={(e) => { e.preventDefault(); save.mutate({ row: { kind: adding, fact, ...(adding === "score" ? { points: Number(points) || 10 } : {}) } }); }}
        >
          <label className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">{adding === "score" ? "A fact worth points" : "A fact that makes a no"}</span>
            <input value={fact} onChange={(e) => setFact(e.target.value)} className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px]" />
          </label>
          {adding === "score" ? (
            <label className="flex flex-col gap-1 w-20">
              <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Points</span>
              <input value={points} onChange={(e) => setPoints(e.target.value.replace(/\D/g, ""))} className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px] tabular-nums" />
            </label>
          ) : null}
          <button type="submit" className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold">Add</button>
          <button type="button" onClick={() => setAdding(null)} className="px-3 py-1.5 rounded-lg border border-line-2 text-xs font-medium">Cancel</button>
        </form>
      ) : null}
    </Card>
  );
}

export function Row({ row, mayEdit, onRemove }: { row: JudgeRowView; mayEdit: boolean; onRemove: () => void }) {
  return (
    <tr className="border-b border-line">
      <td className="py-1.5">{row.fact}</td>
      <td className="py-1.5 text-right tabular-nums text-good">+{row.points}</td>
      <td className="py-1.5 text-right">{mayEdit ? <button onClick={onRemove} className="text-xs text-muted">Remove</button> : null}</td>
    </tr>
  );
}
